import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Archive, BookOpen, Download, FileSpreadsheet, Lock } from 'lucide-react';

import { exportsAPI, messageErreurExport } from '../lib/exportsApi';
import { formatDateHeure } from '../lib/formats';
import { useAuth } from '../context/AuthContext';
import { Aide } from '../components/ui/aide';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { toast } from '../components/ui/sonner';

const ETATS_ARCHIVE = {
  queued: 'En attente',
  running: 'En préparation',
  done: 'Prête',
  failed: 'Échec',
  expired: 'Expirée',
};
const EN_COURS = ['queued', 'running'];
const DELAI_SUIVI_MS = 3000;

const Bloc = ({ titre, aide, children, testid }) => (
  <section className="rounded-xl border border-border bg-card p-4 shadow-sm" data-testid={testid}>
    <div className="mb-3 flex items-center gap-2">
      <h2 className="text-sm font-semibold text-foreground">{titre}</h2>
      {aide ? <Aide titre={titre}>{aide}</Aide> : null}
    </div>
    {children}
  </section>
);

const ComptesDialog = ({ open, onOpenChange, comptes, onSave, enCours }) => {
  const [codes, setCodes] = useState({});
  useEffect(() => {
    if (open) setCodes(Object.fromEntries(comptes.map((c) => [c.key, c.code])));
  }, [open, comptes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Codes de compte
            <Aide titre="Codes de compte">
              Plan SYSCOHADA proposé par défaut. Remplacez un code par celui de votre comptable: seuls les codes changés sont gardés.
            </Aide>
          </DialogTitle>
          <DialogDescription className="sr-only">Codes de compte de l'export comptable</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {comptes.map((compte) => (
            <div key={compte.key} className="grid grid-cols-[1fr_8rem] items-center gap-3">
              <Label htmlFor={`code-${compte.key}`} className="text-sm">
                {compte.label}
              </Label>
              <Input
                id={`code-${compte.key}`}
                inputMode="numeric"
                className="h-8 text-right tabular-nums"
                value={codes[compte.key] ?? ''}
                onChange={(e) => setCodes((avant) => ({ ...avant, [compte.key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button type="button" disabled={enCours} onClick={() => onSave(codes)}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExportsPage = () => {
  const { canSeeMoney, isAdminSociete, isSuperAdmin } = useAuth();
  const peutArchiver = Boolean(isAdminSociete || isSuperAdmin);

  const [chargement, setChargement] = useState(true);
  const [jeux, setJeux] = useState([]);
  const [enCours, setEnCours] = useState('');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [comptes, setComptes] = useState([]);
  const [comptesOuverts, setComptesOuverts] = useState(false);
  const [archive, setArchive] = useState(null);
  const minuteur = useRef(null);

  const lireArchive = useCallback(async () => {
    try {
      const travail = await exportsAPI.derniereArchive();
      setArchive(travail);
      return travail;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const reponse = await exportsAPI.jeux();
        if (!annule) setJeux(reponse.datasets || []);
      } catch (error) {
        if (!annule) toast.error(await messageErreurExport(error, 'La liste des exports est indisponible.'));
      }
      if (peutArchiver && !annule) await lireArchive();
      if (!annule) setChargement(false);
    })();
    return () => {
      annule = true;
    };
  }, [peutArchiver, lireArchive]);

  // Tant que l'archive se prépare, on relit son état; la page reste utilisable.
  useEffect(() => {
    if (archive && EN_COURS.includes(archive.status)) {
      minuteur.current = setTimeout(lireArchive, DELAI_SUIVI_MS);
    }
    return () => clearTimeout(minuteur.current);
  }, [archive, lireArchive]);

  const telecharger = async (cle, action, repli) => {
    setEnCours(cle);
    try {
      await action();
      toast.success('Le fichier est téléchargé.');
    } catch (error) {
      toast.error(await messageErreurExport(error, repli));
    } finally {
      setEnCours('');
    }
  };

  const ouvrirComptes = async () => {
    try {
      setComptes(await exportsAPI.comptes());
      setComptesOuverts(true);
    } catch (error) {
      toast.error(await messageErreurExport(error, 'Les codes de compte sont indisponibles.'));
    }
  };

  const enregistrerComptes = async (codes) => {
    setEnCours('comptes');
    try {
      setComptes(await exportsAPI.enregistrerComptes(codes));
      setComptesOuverts(false);
      toast.success('Les codes de compte sont enregistrés.');
    } catch (error) {
      toast.error(await messageErreurExport(error, "Les codes n'ont pas pu être enregistrés."));
    } finally {
      setEnCours('');
    }
  };

  const lancerArchive = async () => {
    setEnCours('archive');
    try {
      setArchive(await exportsAPI.lancerArchive());
    } catch (error) {
      toast.error(await messageErreurExport(error, "L'archive n'a pas pu être lancée."));
    } finally {
      setEnCours('');
    }
  };

  if (chargement) return <LoadingState variant="cards" />;

  const archivePrete = archive?.status === 'done';

  return (
    <div className="space-y-4" data-testid="exports-page">
      <PageHeader eyebrow="Rapports" title="Exports" />

      {jeux.length === 0 ? (
        <EmptyState icon={Lock} title="Aucun export n'est ouvert à votre profil" />
      ) : (
        <Bloc
          titre="Mes données en Excel"
          testid="bloc-jeux"
          aide="Un fichier Excel par liste, avec les mêmes colonnes que l'écran. Les montants ne sont proposés qu'à la direction."
        >
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {jeux.map((jeu) => (
              <li key={jeu.name} className="flex items-center justify-between gap-2 rounded-lg border border-border/70 px-3 py-2">
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="truncate">{jeu.title}</span>
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={enCours === jeu.name}
                  aria-label={`Télécharger ${jeu.title}`}
                  onClick={() => telecharger(jeu.name, () => exportsAPI.telechargerJeu(jeu.name), "Le fichier n'a pas pu être préparé.")}
                >
                  <Download aria-hidden="true" />
                  Excel
                </Button>
              </li>
            ))}
          </ul>
        </Bloc>
      )}

      {canSeeMoney ? (
        <Bloc
          titre="Export comptable"
          testid="bloc-compta"
          aide="Les factures, avoirs, encaissements, dépenses et règlements de la période, en écritures au plan SYSCOHADA. Les opérations qui ne peuvent pas devenir une écriture sont listées dans la feuille « Non repris »."
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="export-du" className="text-xs">Du</Label>
              <Input id="export-du" type="date" className="h-8 w-40" value={du} onChange={(e) => setDu(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="export-au" className="text-xs">Au</Label>
              <Input id="export-au" type="date" className="h-8 w-40" value={au} onChange={(e) => setAu(e.target.value)} />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={enCours === 'compta'}
              onClick={() => telecharger('compta', () => exportsAPI.telechargerEcritures({ du, au }), "Les écritures n'ont pas pu être préparées.")}
            >
              <Download aria-hidden="true" />
              {enCours === 'compta' ? 'Préparation…' : 'Télécharger les écritures'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={ouvrirComptes}>
              <BookOpen aria-hidden="true" />
              Codes de compte
            </Button>
          </div>
        </Bloc>
      ) : null}

      {peutArchiver ? (
        <Bloc
          titre="Toutes mes données"
          testid="bloc-archive"
          aide="Une archive avec un fichier Excel par domaine (flotte, exploitation, facturation, finance, stocks, maintenance, locations) et les écritures comptables. Elle se prépare en arrière-plan et reste disponible 24 heures."
        >
          <div className="flex flex-wrap items-center gap-3">
            {archive ? (
              <span className="text-sm" data-testid="etat-archive">
                {ETATS_ARCHIVE[archive.status] || 'En attente'}
                {archivePrete && archive.expires_at ? ` — valable jusqu'au ${formatDateHeure(archive.expires_at)}` : ''}
              </span>
            ) : null}
            {archivePrete ? (
              <Button
                type="button"
                size="sm"
                disabled={enCours === 'telechargement'}
                onClick={() => telecharger('telechargement', () => exportsAPI.telechargerArchive(archive.id), "L'archive n'est plus disponible.")}
              >
                <Archive aria-hidden="true" />
                Télécharger l'archive
              </Button>
            ) : null}
            {!archive || !EN_COURS.includes(archive.status) ? (
              <Button type="button" size="sm" variant="outline" disabled={enCours === 'archive'} onClick={lancerArchive}>
                {archivePrete ? 'Préparer de nouveau' : "Préparer l'archive"}
              </Button>
            ) : null}
          </div>
          {archive?.failed?.length ? (
            <p className="mt-2 text-xs text-destructive" data-testid="archive-incomplete">
              Non exportés: {archive.failed.join(', ')}.
            </p>
          ) : null}
          {archive?.status === 'failed' && archive.error ? (
            <p className="mt-2 text-xs text-destructive">{archive.error}</p>
          ) : null}
        </Bloc>
      ) : null}

      <ComptesDialog
        open={comptesOuverts}
        onOpenChange={setComptesOuverts}
        comptes={comptes}
        onSave={enregistrerComptes}
        enCours={enCours === 'comptes'}
      />
    </div>
  );
};

export default ExportsPage;
